import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import StagedActionSchema, { StagedActionJSON, StagedActionParams } from "ai-assistant/staged-actions/staged-action-schema";
import { StagedActionState, isPending } from "ai-assistant/staged-actions/statuses/staged-action-state";

export interface StagedActionDetail {
  label: string;
  value: string;
}

/**
 * Work the assistant has prepared and the user has not yet approved.
 *
 * Nothing has happened yet. The summary and details are written by the server from the
 * arguments it validated, not by the model, so what is rendered here is what will actually
 * run if the user confirms.
 */
export default class StagedAction {
  public static create(values: StagedActionParams): StagedAction {
    const parsed = StagedActionSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid StagedAction parameters.");
    }
    return new StagedAction(parsed);
  }

  public static fromJSON(json: StagedActionJSON): StagedAction {
    if (isNullOrUndefined(json)) return json;
    const params = StagedActionSchema.deserialize(json);
    return new StagedAction(params);
  }

  public readonly id: Guid;
  public readonly capability: string;
  public readonly summary: string;
  public readonly details: StagedActionDetail[];
  public readonly expiresAt: AHDate;
  public readonly state: StagedActionState;

  /**
   * Whether a preview built earlier still describes the world well enough to approve.
   *
   * Checked here as well as on the server. The server's answer is the authority; this
   * stops the UI offering a button that was always going to be refused.
   */
  public get hasExpired(): boolean {
    return this.expiresAt.isBefore(new AHDate());
  }

  /** Whether this can still be confirmed or cancelled. */
  public get isPending(): boolean {
    return isPending(this.state) && !this.hasExpired;
  }

  public constructor(params: StagedActionParams) {
    this.id = params.id;
    this.capability = params.capability;
    this.summary = params.summary;
    this.details = params.details;
    this.expiresAt = params.expiresAt;
    this.state = params.state;
  }

  /**
   * The same preview, retired because something newer replaced it.
   *
   * Staging a new action retires any the user left unanswered in the same conversation, in the
   * same transaction as the new one - so by the time a reply carrying a new preview arrives, the
   * server has already moved the older ones here. It never says so, because a turn carries only
   * the actions it staged, and a client that goes on offering Confirm on an older card offers a
   * button the server will refuse. `Superseded` rather than `Expired`: nobody walked away, we
   * retired it.
   */
  public asSuperseded(): StagedAction {
    return new StagedAction({
      id: this.id,
      capability: this.capability,
      summary: this.summary,
      details: this.details,
      expiresAt: this.expiresAt,
      state: StagedActionState.Superseded,
    });
  }

  public toJSON(): StagedActionJSON {
    return StagedActionSchema.serialize(this);
  }

  public isEqualTo(other: StagedAction): boolean {
    return this.id.isEqualTo(other.id) && this.state === other.state;
  }

  public toString(): string {
    return `${this.capability}: ${this.summary} (${StagedActionState[this.state]})`;
  }
}
